/**
 * Domain Layer - TemplateSlotPreviewPolicy
 * 解放枠テンプレートのプレビュー算出ポリシー
 *
 * @description
 * テンプレート定義から「1時間分の区間展開」「区間あたりのカテゴリ構成」を導く純粋なルール。
 * 表示用の時刻文字列は組み立てず分オフセットのみを返す（時刻表示はPresentation層の責務）。
 */

import type { CategoryCounts } from './ReservationSlotSummary';
import type { ReservationSlotTemplate } from './ReservationSlotTemplate';
import type { TreatmentCategory } from './TreatmentCategory';

/**
 * 1時間あたりの分数
 */
const MINUTES_PER_HOUR = 60;

/**
 * 交互パターンにおける区間の種別
 */
export type IntervalPattern = 'A' | 'B';

/**
 * 1時間を枠間隔で分割した1区間
 *
 * @property {number} index - 区間の連番（0始まり）
 * @property {number} startMinute - 時の開始からの経過分（0/15/30/45 など）
 * @property {number} endMinute - 時の開始からの終了分
 * @property {IntervalPattern | null} pattern - 交互パターンの種別（単一パターンはnull）
 * @property {TreatmentCategory[]} slots - この区間で生成される各枠のカテゴリ
 */
export interface HourInterval {
  index: number;
  startMinute: number;
  endMinute: number;
  pattern: IntervalPattern | null;
  slots: TreatmentCategory[];
}

/**
 * 区間あたりのカテゴリ構成
 *
 * @property {CategoryCounts} a - Aパターンのカテゴリ別枠数
 * @property {CategoryCounts | null} b - Bパターンのカテゴリ別枠数（単一パターンはnull）
 */
export interface IntervalCategorySummary {
  a: CategoryCounts;
  b: CategoryCounts | null;
}

/**
 * カテゴリ配列をカテゴリ別の件数へ集計する
 *
 * @param {readonly TreatmentCategory[]} categories - カテゴリ配列
 * @returns {CategoryCounts} カテゴリ別の件数
 */
const countCategories = (categories: readonly TreatmentCategory[]): CategoryCounts => {
  const counts: CategoryCounts = {};
  for (const category of categories) {
    counts[category] = (counts[category] ?? 0) + 1;
  }
  return counts;
};

/**
 * 交互パターンとして展開できるか判定する
 *
 * @description
 * patternTypeが'alternate'でもslotsBが未設定のデータが存在しうるため、
 * その場合は単一パターン扱い（slotsAのみ）にフォールバックする。
 *
 * @param {ReservationSlotTemplate} template - 対象テンプレート
 * @returns {boolean} 交互パターンとして展開できる場合true
 */
const hasAlternatePattern = (template: ReservationSlotTemplate): boolean =>
  template.patternType === 'alternate' &&
  template.slotsB !== null &&
  template.slotsB.length > 0;

/**
 * テンプレートを1時間分の区間へ展開する
 *
 * @description
 * 枠間隔15分なら4区間、30分なら2区間に分割する。
 * 交互パターンの場合、偶数番目の区間にA・奇数番目の区間にBを割り当てる。
 *
 * @param {ReservationSlotTemplate} template - 対象テンプレート
 * @returns {HourInterval[]} 1時間分の区間配列
 */
export const expandTemplateToHour = (template: ReservationSlotTemplate): HourInterval[] => {
  const intervalCount = Math.max(1, Math.floor(MINUTES_PER_HOUR / template.intervalMinutes));
  const alternate = hasAlternatePattern(template);
  const intervals: HourInterval[] = [];

  for (let index = 0; index < intervalCount; index += 1) {
    const isB = alternate && index % 2 === 1;
    const slots = isB ? template.slotsB ?? template.slotsA : template.slotsA;
    intervals.push({
      index,
      startMinute: index * template.intervalMinutes,
      endMinute: (index + 1) * template.intervalMinutes,
      pattern: alternate ? (isB ? 'B' : 'A') : null,
      slots: [...slots],
    });
  }

  return intervals;
};

/**
 * 区間あたりのカテゴリ構成を集計する
 *
 * @description
 * 「ピル ×2」「ピル ×2 / ウェルクリ ×1」のような構成表示に使用する。
 *
 * @param {ReservationSlotTemplate} template - 対象テンプレート
 * @returns {IntervalCategorySummary} A・B各パターンのカテゴリ別枠数
 */
export const summarizeIntervalCategories = (
  template: ReservationSlotTemplate
): IntervalCategorySummary => ({
  a: countCategories(template.slotsA),
  b: hasAlternatePattern(template) ? countCategories(template.slotsB ?? []) : null,
});

/**
 * 1時間あたりに生成される枠の総数を算出する
 *
 * @param {ReservationSlotTemplate} template - 対象テンプレート
 * @returns {number} 1時間あたりの枠数
 */
export const countSlotsPerHour = (template: ReservationSlotTemplate): number =>
  expandTemplateToHour(template).reduce((total, interval) => total + interval.slots.length, 0);
